import { memo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import Map from './Map/Map';
import MapCard from './MapCard';
import ContentWrapper from '../../common/ContentWrapper';
import { RegionEnum } from '../../../core/japaneseRegions';
import HotelCount from '../../../../types/HotelCount';
import useTotalHotelCount from '../../../core/hooks/useTotalHotelCount';

type Props = {
  hotelCount: HotelCount | number | undefined;
  onSelectionChanged: (type: string, value: number) => void;
  onClickArea: (area: string) => void;
  searchQuery: string;
};

const MapSelector = ({ hotelCount, onSelectionChanged, onClickArea, searchQuery }: Props) => {
  const { t } = useTranslation('home');
  const [selectedArea, setSelectedArea] = useState(RegionEnum.Kanto); // default - Kanto

  const getSelectionChangedHandler = (type: string) => (value: number) => {
    onSelectionChanged(type, value);
    setSelectedArea(value);
  };

  const count = useTotalHotelCount(hotelCount);

  return (
    <ContentWrapper className="justify-center pt-12 sm:pt-11 md:pt-0 lg:pt-4">
      <div className="h-76 w-full gap-10 rounded-xl bg-teal-50 sm:grid sm:h-112 sm:grid-cols-2 sm:px-10 lg:px-20">
        <div className="relative hidden sm:flex">
          {!!count && (
            <div className="absolute pt-12 sm:size-36">
              <div className="flex size-36 flex-col items-center justify-evenly rounded-full bg-gray-600 p-4 text-white">
                <b className="text-lg">{t('component:map_selector.whole_country')}</b>
                <b className="text-5xl">{count}</b>
                <b className="text-lg">{t('component:map_selector.facility')}</b>
              </div>
            </div>
          )}
          <Map
            selectedArea={selectedArea}
            setSelectedArea={getSelectionChangedHandler('desktop')}
          />
        </div>
        <MapCard selectedArea={selectedArea} onClickArea={onClickArea} searchQuery={searchQuery} />

        {/* mobile view */}
        <div className="relative flex sm:hidden">
          {!!count && (
            <div className="absolute left-12 pt-8">
              <div className="flex size-28 flex-col items-center justify-evenly rounded-full bg-gray-600 p-4 text-white">
                <b className="text-sm">{t('component:map_selector.whole_country')}</b>
                <b className="text-4xl">{count}</b>
                <b className="text-sm">{t('component:map_selector.facility')}</b>
              </div>
            </div>
          )}
          <Map
            mobile
            selectedArea={selectedArea}
            setSelectedArea={getSelectionChangedHandler('mobile')}
          />
        </div>
      </div>
    </ContentWrapper>
  );
};

export default memo(MapSelector);
